import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot, createUrlTreeFromSnapshot } from '@angular/router';
import { AuthService, AuthUser, Roles, RouteFns } from '@core';
import { AccountEntityManagerProvider } from '@data';
import { ToastrService } from 'ngx-toastr';
import { AccountDbQueryService } from './account-db-query.service';
import { ProximityRightEnum } from '@models';

/** path to user edit page, so user can edit themselves. Obsolete, because self-edit should be in account-user path */
// const userEditRegex = /\/accounts\/[a-z0-9-]+\/manage\/account-users\/[a-z0-9-]+$/;

export const accountCanActivateGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const emProvider = inject(AccountEntityManagerProvider);

  emProvider.prepare();
  const dbQueryService = inject(AccountDbQueryService);
  const toastr = inject(ToastrService);

  const isLoggedIn = await authService.redirectIfNotLoggedIn(state.url);
  if (!isLoggedIn) {
    return false;
  }

  const user = (await authService.getLoggedInUser()) as AuthUser;
  const rights = state.url.startsWith('/account-user/')
    ? [ProximityRightEnum.CanCreateJobOrders, ProximityRightEnum.CanEditJobOrderByApprovalTree]
    : state.url.startsWith('/account-admin/')
      ? // Fallback right-set for /account-admin/... routes that don't declare their own data.rights.
        // Every Account-tier admin (PAA + AccountAdmin + ApprovalAdmin + OrderAdmin + SupportAdmin)
        // holds CanEditUserByApprovalTree, so this is the "you belong under /account-admin/" gate.
        // Routes that need Approve or Edit-JO must declare their own data.rights (which take precedence).
        [ProximityRightEnum.CanEditUserByApprovalTree]
      : [
          // Fallback right-set for /account/... routes that don't declare their own data.rights.
          // Scoped AccountAdmins hold CanEditUserByApprovalTree; per-route guards further down
          // the chain (e.g. account-users) are what genuinely scope them — this just lets them
          // PAST the chain's topmost gate so the more specific guards can evaluate.
          ProximityRightEnum.CanManageAccountAdministration,
          ProximityRightEnum.CanManageAccountOrganization,
          ProximityRightEnum.CanEditProgram,
          ProximityRightEnum.CanEditUserByApprovalTree,
        ];
  const ok = RouteFns.checkRoutePermissions(route, rights, user, toastr);

  // Training Mode: a locked PAA hitting Account Management (/account/accounts/{id}/manage*)
  // is redirected to the locked landing, and an unlocked PAA on /locked is sent back to /manage.
  // Only PAAs navigating those specific routes incur a live server check — this keeps the
  // lock state accurate mid-session (a GlobalAdmin can lock/unlock and it takes effect on
  // the PAA's next navigation, no re-login needed). Other "/manage" segments
  // (e.g. /account-admin/accounts/{id}/manage*) are unrelated and must NOT be blocked.
  // hasOrigRole, not hasRole: impersonating replaces user.role with the target's, so a locked PAA
  // acting as someone else would otherwise skip this check entirely and walk into Account
  // Management. The server answers about whoever is SIGNED IN, so asking is always safe — a
  // GlobalAdmin acting as a PAA still comes back unlocked.
  if (ok && user.accountId && user.hasOrigRole(Roles.PrincipalAccountAdmin)) {
    const isAccountManageRoute = /^\/account\/accounts\/[^/]+\/manage(\/|$)/.test(state.url);
    const isLockedRoute = /^\/account\/accounts\/[^/]+\/locked(\/|$)/.test(state.url);
    if (isAccountManageRoute || isLockedRoute) {
      const isLocked = await dbQueryService.getMyTrainingLockState();
      // Keep the cached flag in sync so the rest of the app (menu, etc.) reflects current state.
      user.isTrainingLocked = isLocked;
      if (isLocked && isAccountManageRoute) {
        return createUrlTreeFromSnapshot(route.root, ['/account/accounts', user.accountId, 'locked']);
      }
      if (!isLocked && isLockedRoute) {
        return createUrlTreeFromSnapshot(route.root, ['/account/accounts', user.accountId, 'manage']);
      }
    }
  }

  if (ok) {
    if (user.accountId && !route.paramMap.has('accountId')) {
      // add accountId to URL params; navigates to account-specific page instead of account chooser
      // e.g. /transaction/account -> /transaction/account/:accountId
      return createUrlTreeFromSnapshot(route, [user.accountId], route.queryParams);
    } else if (user.accountId && route.paramMap.get('accountId') != user.accountId) {
      // replace accountId in URL params; navigates to correct page for account
      // only really happens in impersonation scenarios
      return createUrlTreeFromSnapshot(route.parent || route.root, ['accounts', user.accountId]);
    } else if (user.accountUserId && route.paramMap.has('accountUserId') && route.paramMap.get('accountUserId') != user.accountUserId) {
      // Trying to navigate to another user's page - go back to home
      if (!user.isAccountAdmin) {
        return createUrlTreeFromSnapshot(route.root, ['/']);
      }
    }
    // insure that all lookups are cached.
    await dbQueryService.cacheAllLookups();
  } else {
    // navigate to a safe place
    return createUrlTreeFromSnapshot(route.root, ['/']);
  }
  return ok;
};
